import type { AnchorHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

import { BrandLogo, cn, focusRing, MonoLabel } from '@/design-system';

import { authCopy } from './copy';

const authLinkClass =
    'inline-flex min-h-[var(--control-h-sm)] items-center rounded-[2px] font-[family-name:var(--font-body)] text-[13px] leading-none text-[var(--accent)] no-underline hover:text-[var(--accent-dark)] hover:underline';

/** Een tekstlink in de stijl van de ghost-knop ("Wachtwoord vergeten?"). */
export function AuthLink({ className, ...props }: LinkProps) {
    return <Link className={cn(authLinkClass, focusRing, className)} {...props} />;
}

/** Dezelfde tekstlink naar een adres buiten de app, zoals de website. */
export function AuthExternalLink({
    className,
    href,
    children,
    ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; children: ReactNode }) {
    return (
        <a href={href} className={cn(authLinkClass, focusRing, className)} {...props}>
            {children}
        </a>
    );
}

/**
 * De omlijsting van alles vóór de app: inloggen, omgeving kiezen, wachtwoord
 * vergeten of herstellen en een uitnodiging accepteren.
 *
 * Twee kolommen die onder ~720 px onder elkaar vallen: links het donkere
 * paneel met de claim, rechts een gecentreerde kolom van 380 px. Elk scherm
 * vult alleen die rechterkolom, zodat ze er als één reeks uitzien.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
    const rootStyle = { containerType: 'inline-size' } as CSSProperties;

    return (
        <div
            style={rootStyle}
            className="flex min-h-full w-full flex-wrap overflow-auto bg-[var(--ink-900)]"
        >
            <section className="flex min-w-[300px] flex-[1_1_420px] flex-col justify-between gap-10 border-r border-[var(--ink-600)] bg-[linear-gradient(180deg,var(--ink-login-gradient-top)_0%,var(--ink-900)_100%)] px-[clamp(24px,7cqi,56px)] py-[clamp(32px,8cqi,64px)]">
                {/* Het logo op de donkere ondergrond: "Smart" wit, beeldmerk en "keur" groen. */}
                <BrandLogo surface="dark" height={24} title={authCopy.brand} />

                <div className="flex max-w-[460px] flex-col gap-7">
                    <p className="m-0 font-[family-name:var(--font-display)] text-[clamp(30px,7cqi,44px)] leading-[1.08] font-semibold tracking-[-0.03em] text-pretty text-[var(--text-on-dark)]">
                        {authCopy.claim}
                    </p>
                    <p className="m-0 text-[16px] leading-[1.55] text-[var(--text-on-dark-3)]">
                        {authCopy.claimBody}
                    </p>
                    <ul className="m-0 flex list-none flex-col gap-[10px] p-0">
                        {authCopy.bullets.map((bullet) => (
                            <li
                                key={bullet}
                                className="flex items-center gap-[10px] text-[14px] leading-[1.4] text-[var(--text-on-dark-2)]"
                            >
                                <span
                                    aria-hidden="true"
                                    className="h-[6px] w-[6px] shrink-0 rounded-full bg-[var(--accent)]"
                                />
                                {bullet}
                            </li>
                        ))}
                    </ul>
                </div>

                <MonoLabel
                    size={11}
                    className="tracking-[0.04em] text-[var(--text-on-dark-6-safe)]"
                >
                    {authCopy.legal}
                </MonoLabel>
            </section>

            <section className="flex min-w-[300px] flex-[1_1_400px] items-center justify-center bg-[var(--surface-page)] px-[clamp(20px,5cqi,40px)] py-[clamp(28px,5cqi,40px)]">
                <div className="flex w-full max-w-[380px] flex-col gap-[22px]">{children}</div>
            </section>
        </div>
    );
}

/** Kop en ondertitel van een auth-scherm. */
export function AuthHeading({
    title,
    body,
    eyebrow,
}: {
    title: ReactNode;
    body?: ReactNode;
    eyebrow?: ReactNode;
}) {
    return (
        <div>
            {eyebrow ? (
                <MonoLabel size={10} className="mb-2 block">
                    {eyebrow}
                </MonoLabel>
            ) : null}
            <h1 className="m-0 mb-[6px] font-[family-name:var(--font-display)] text-[26px] leading-[1.2] font-semibold tracking-[-0.02em] text-[var(--text-primary)]">
                {title}
            </h1>
            {body ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {body}
                </p>
            ) : null}
        </div>
    );
}

/** Een bevestiging of uitleg boven een formulier. */
export function AuthNotice({ children }: { children: ReactNode }) {
    return (
        <p role="status" className="m-0 text-[13px] leading-[1.5] text-[var(--accent-dark)]">
            {children}
        </p>
    );
}

/**
 * Een fout die het hele formulier betreft. Geen verbinding is geen fout van de
 * gebruiker: die melding is amber, nooit rood.
 */
export function AuthError({
    children,
    tone = 'danger',
}: {
    children: ReactNode;
    tone?: 'danger' | 'offline';
}) {
    return (
        <p
            role="alert"
            className={
                tone === 'offline'
                    ? 'm-0 rounded-[7px] border border-[var(--warn-border)] bg-[var(--warn-tint)] px-3 py-[9px] text-[13px] leading-[1.4] text-[var(--warn-text)]'
                    : 'm-0 text-[13px] leading-[1.4] text-[var(--danger)]'
            }
        >
            {children}
        </p>
    );
}
